import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import { FULL_MEMBER_ROLES } from "../constants";

// Channel membership + workspace-ownership actions. Spread into
// `actionsMutators` (actions.ts). Space-scoped joins/leaves are intentionally
// rejected here and routed through the server endpoints so their cascades run.
export const membershipActionMutators = {
  // Join a channel — self-join only, FULL_MEMBER_ROLES required
  joinChannel: defineMutator(
    z.object({
      channelId: z.string(),
      userId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.userId !== ctx.sub) {
        throw new Error("Unauthorized: userId mismatch");
      }
      if (tx.location === "server") {
        const channel = await tx.run(
          zql.channels.where("id", args.channelId).one(),
        );
        if (!channel) throw new Error("Channel not found");
        // Spaces (channels.type='space', post mig 120) require admin-only
        // membership grants via the server endpoint
        // (POST /api/client-spaces/:id/join + members). Round 3 closed
        // the same hole on `channelMembersMutators.insert` but missed
        // this action mutator, which calls `tx.mutate.channel_members.
        // upsert` directly (table-level write, bypasses the mutator-side
        // guard). Without this rejection, any full workspace member
        // could `mutators.actions.joinChannel({ channelId: <space_id> })`
        // to self-grant space membership, bypassing audit logging,
        // notification dispatch, and the auto-join cascade. Round 4.
        if (channel.type === "space") {
          throw new Error(
            "Unauthorized: space membership goes through the server endpoint",
          );
        }
        if (channel.is_private) {
          throw new Error("Cannot self-join private channel");
        }
        if (channel.is_archived) {
          throw new Error("Cannot join archived channel");
        }
        if (!channel.workspace_id) {
          throw new Error("Cannot self-join DM");
        }
        const wsMember = await tx.run(
          zql.workspace_members
            .where("workspace_id", channel.workspace_id)
            .where("user_id", ctx.sub)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (
          !wsMember ||
          !(FULL_MEMBER_ROLES as readonly string[]).includes(wsMember.role)
        ) {
          throw new Error("Unauthorized: not a full workspace member");
        }
      }
      // Use deterministic ID so upsert matches an existing membership
      // instead of always inserting a duplicate row. Clear removed_at to
      // handle the rejoin-after-kick case cleanly.
      const membershipId = `${args.channelId}:${args.userId}`;
      await tx.mutate.channel_members.upsert({
        id: membershipId,
        channel_id: args.channelId,
        user_id: args.userId,
        is_channel_manager: false,
        joined_at: Date.now(),
        muted: false,
        notify: "all",
        hidden: false,
        removed_at: null,
        removed_by: null,
      });
    },
  ),

  // Leave a channel (soft-delete: sets removed_at + removed_by + clears
  // is_channel_manager so the tombstone-cleared invariant holds).
  leaveChannel: defineMutator(
    z.object({
      membershipId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const membership = await tx.run(
          zql.channel_members.where("id", args.membershipId).one(),
        );
        if (!membership) return;
        if (membership.user_id !== ctx.sub) {
          throw new Error("Unauthorized: can only leave own memberships");
        }
        // Spaces require the server endpoint
        // (DELETE /api/client-spaces/:id/members/:userId) so the cascade
        // unjoin to child channels runs. Round-5 self-review — same
        // class as `channelMembersMutators.delete` (channels.ts).
        // Without this guard, a user self-leaving a space via this
        // mutator would un-join the space but stay subscribed to its
        // child channels.
        const channel = await tx.run(
          zql.channels.where("id", membership.channel_id).one(),
        );
        if (channel?.type === "space") {
          throw new Error(
            "Unauthorized: leaving a space requires the server endpoint",
          );
        }
      }
      // Clear `is_channel_manager` alongside the soft-delete so a manager
      // who voluntarily leaves and later re-joins (via channel invite or
      // POST /channels/:id/members) doesn't silently regain manager
      // privileges. The rejoin paths' `ON CONFLICT DO UPDATE SET` clauses
      // intentionally omit `is_channel_manager` to preserve role for
      // ACTIVE members; tombstones must therefore carry the cleared flag
      // so resurrection picks up as a regular member. Matches the
      // invariant enforced by `channelMembersMutators.delete` (channels.ts)
      // and the workspace-kick trigger (mig 103/115).
      await tx.mutate.channel_members.update({
        id: args.membershipId,
        is_channel_manager: false,
        removed_at: Date.now(),
        removed_by: ctx?.sub,
      });
    },
  ),

  // Mute/unmute a channel
  toggleChannelMute: defineMutator(
    z.object({
      membershipId: z.string(),
      muted: z.boolean(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const membership = await tx.run(
          zql.channel_members.where("id", args.membershipId).one(),
        );
        if (!membership) return;
        if (membership.user_id !== ctx.sub) {
          throw new Error("Unauthorized: can only mute own memberships");
        }
      }
      await tx.mutate.channel_members.update({
        id: args.membershipId,
        muted: args.muted,
      });
    },
  ),

  // Transfer primary_owner from the authenticated caller to another
  // workspace member. The caller is always derived from ctx.sub — never
  // from caller-supplied args — so a malformed JWT or auth misconfig
  // can't be abused to spoof the from-user. Calls
  // tx.mutate.workspace_members.update directly so it bypasses the
  // "cannot change own role" and "cannot set primary_owner" guards in
  // the public workspace_members.update mutator. Authority comes from
  // the role check below (caller must be the current primary_owner).
  transferOwnership: defineMutator(
    z.object({
      workspaceId: z.string(),
      toUserId: z.string(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.toUserId === ctx.sub) {
        throw new Error("Cannot transfer ownership to self");
      }
      if (tx.location === "server") {
        // Defensive: ctx.sub must be set on server. The auth middleware
        // enforces this, but we fail closed instead of falling through
        // to a query that would trust args.
        if (!ctx?.sub) {
          throw new Error("Unauthorized: missing caller identity");
        }
        const fromMember = await tx.run(
          zql.workspace_members
            .where("workspace_id", args.workspaceId)
            .where("user_id", ctx.sub)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (!fromMember || fromMember.role !== "primary_owner") {
          throw new Error("Unauthorized: caller is not primary owner");
        }
        const toMember = await tx.run(
          zql.workspace_members
            .where("workspace_id", args.workspaceId)
            .where("user_id", args.toUserId)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (!toMember) {
          throw new Error("Target user is not a workspace member");
        }
        // Demote first, then promote — safe under current schema
        // (no partial unique constraint on primary_owner).
        await tx.mutate.workspace_members.update({
          id: fromMember.id,
          role: "owner",
        });
        await tx.mutate.workspace_members.update({
          id: toMember.id,
          role: "primary_owner",
        });
      }
    },
  ),
};
